import type { ClientRect, Modifier } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';

interface SoftClampConfig {
    boundingRect: ClientRect; // The table's bounding rect
    threshold: number; // Number of px beyond boundingRect we allow before friction
    friction: number; // 0 < friction < 1 for partial movement
}

/**
 * Soft clamp modifier: restrict movement to boundingRect +/- threshold with friction.
 * Instead of using the dragged <th>'s rect (which is small), we create an effective dragging rect
 * that uses the table's bottom. That way, dragging downward is measured relative to the full table height,
 * while dragging upward uses the small header area.
 */
export function createSoftClampModifier({
    boundingRect,
    threshold,
    friction,
}: SoftClampConfig): Modifier {
    return ({ draggingNodeRect, transform }) => {
        if (!draggingNodeRect) {
            return transform;
        }

        // Create an effective dragging rect by replacing the actual bottom/height
        // with that of the table (boundingRect)
        const effectiveRect = createEffectiveDraggingNodeRect(draggingNodeRect, boundingRect);

        // 1) Expand boundingRect (our full table area) by threshold
        const extendedRect = extendRect(boundingRect, threshold);

        // 2) Clamp the transform so that the effective dragged element doesn't leave the extended table area.
        const restricted = restrictToBoundingRect(transform, effectiveRect, extendedRect);
        const { x, y } = restricted;

        // 3) If there's any overshoot beyond extendedRect, apply partial friction.
        const clamped = applyFrictionIfOvershoot({ x, y }, effectiveRect, extendedRect, friction);

        return clamped;
    };
}

/**
 * Create an effective dragging rect that uses the table's vertical bounds.
 * Keep left/right from the original dragged rect, but set:
 * - top = original dragged rect top
 * - bottom = table's bottom
 * - height = table.bottom - original dragged top
 */
function createEffectiveDraggingNodeRect(
    draggingRect: ClientRect,
    tableRect: ClientRect
): ClientRect {
    return {
        top: draggingRect.top,
        left: draggingRect.left,
        right: draggingRect.right,
        bottom: tableRect.bottom,
        width: draggingRect.width,
        height: tableRect.bottom - draggingRect.top,
    };
}

/** Expand boundingRect in all directions by `threshold` */
export function extendRect(rect: ClientRect, threshold: number): ClientRect {
    // Adjust the treshold used for expanding the allowed 'bottom' area you can drag to, as technically our drag element is just the <th> but UX-wise the entire column is dragged.
    // Without this, you could only drag ~44px (height of a <th>) + the treshold before you'd be blocked from dragging -> this is very unrealistic in most tables.
    const adjustedThreshold = threshold + rect.height;

    return {
        top: rect.top - threshold,
        bottom: rect.bottom + adjustedThreshold,
        left: rect.left - threshold,
        right: rect.right + threshold,
        width: rect.width + threshold,
        height: rect.height + threshold,
    };
}

/**
 * If the node overshoots boundingRect, apply partial friction so it gently slides.
 */
function applyFrictionIfOvershoot(
    transform: { x: number; y: number },
    draggingRect: ClientRect,
    boundingRect: ClientRect,
    friction: number
): Transform {
    let { x, y } = transform;

    // Compute current positions of the effective dragged rect:
    const currentLeft = draggingRect.left + x;
    const currentRight = draggingRect.right + x;
    const currentTop = draggingRect.top + y;
    const currentBottom = draggingRect.bottom + y;

    // Overshoot on each boundary:
    const overshootLeft = boundingRect.left - currentLeft;
    const overshootRight = currentRight - boundingRect.right;
    const overshootTop = boundingRect.top - currentTop;
    const overshootBottom = currentBottom - boundingRect.bottom;

    if (overshootLeft > 0) {
        x += overshootLeft * (1 - friction);
    } else if (overshootRight > 0) {
        x -= overshootRight * (1 - friction);
    }

    if (overshootTop > 0) {
        y += overshootTop * (1 - friction);
    } else if (overshootBottom > 0) {
        y -= overshootBottom * (1 - friction);
    }

    return { x, y, scaleX: 1, scaleY: 1 };
}

/**
 * Restrict movement by clamping the dragged element's effective rect within the boundingRect.
 * Similar to dnd-kit’s built-in restrictToBoundingRect.
 */
export function restrictToBoundingRect(
    transform: Transform,
    rect: ClientRect,
    boundingRect: ClientRect
): Transform {
    const value = { ...transform };

    if (rect.top + transform.y <= boundingRect.top) {
        value.y = boundingRect.top - rect.top;
    } else if (rect.bottom + transform.y >= boundingRect.bottom) {
        value.y = boundingRect.bottom - rect.bottom;
    }

    if (rect.left + transform.x <= boundingRect.left) {
        value.x = boundingRect.left - rect.left;
    } else if (rect.right + transform.x >= boundingRect.right) {
        value.x = boundingRect.right - rect.right;
    }

    return value;
}
